import * as THREE from "three";
import Racer from "./racer/racer.js";
import {TrackTracer} from "../track/trackTracer.js";
import { Sound } from "../sound/sound.js"
import {StyleUtils} from "../utils/styleUtils.js";
import {LeaderGhost} from "./racer/leaderGhost.js";

/**
 * All racers
 */
export class Racers {
    static WIDTH_PER_RACER = StyleUtils.getFloat("--racer-spacing");
    static TRACK_LANES_MINIMUM = StyleUtils.getInt("--racer-min-lanes");
    static TRACK_WIDTH_DEFAULT = Racers.TRACK_LANES_MINIMUM * Racers.WIDTH_PER_RACER;

    /**
     * Construct the racers
     * @param {Scene} scene The scene
     * @param {RacersData} data The racers data
     * @param {Track} track The track
     * @param {Function} onFinish A function to call when a racer crosses the finish line
     */
    constructor(scene, data, track, onFinish) {
        this.scene = scene;
        this.racers = [];
        this.finishedRacers = [];
        this.finishedOrder = [];
        this.started = false;
        this.track = track;
        this.onFinish = onFinish;
        this.audioPlayer = new Sound();
        this.sounds = [];
        this.leader = new LeaderGhost();
        this.race_type = track.race_type;

        const racerCount = data.racers.length;
        const trackWidth = Racers.WIDTH_PER_RACER * racerCount;

        for (let racer = 0; racer < racerCount; ++racer)
            this.racers.push(new Racer(
                data.racers[racer],
                new TrackTracer(track),
                new THREE.Vector3(
                    0,
                    .3,
                    trackWidth * (racer / (racerCount - 1) - .5)
                ),racerCount));
    }

    /**
     * Get the required track width for the racers
     * @returns {number} The required track width
     */
    get trackWidth() {
        return Math.max(Racers.TRACK_LANES_MINIMUM, this.racers.length) * Racers.WIDTH_PER_RACER;
    }

    /**
     * Get the number of racers
     * @returns {number} The number of racers
     */
    get racerCount() {
        return this.racers.length;
    }

    /**
     * Get the order of the racers
     * @returns {number[]} The order of the racers
     */
    order(rtype='standard') {
        const racers = [];

        for (let racer = 0, racerCount = this.racers.length; racer < racerCount; ++racer)
            if (this.racers[racer].finished === -1)
                racers.push(this.racers[racer]);

        racers.sort((a, b) => b.moved - a.moved);

        const order = [];

        for (let racer = 0, racerCount = racers.length; racer < racerCount; ++racer) {
            const index = this.racers.indexOf(racers[racer]);

            if (index !== -1)
                order.push(index);
        }

        var combined_order = [...this.finishedOrder, ...order];
        if((rtype == 'reverse')&&(racers.length > 0)) {
            combined_order = combined_order.reverse();
        }

        return combined_order;
    }

    /**
     * Get the player racer
     * @returns {Racer} The player racer
     */
    get player() {
        var racer_found = 0;
        var final_racer = this.racers[0];
        for(var i = 0;i < this.racers.length;i++) {
            if (this.racers[i].current_racer == 1) {
                final_racer = this.racers[i];
                racer_found = 1;
                i = this.racers.length;
            }
        }
        if(racer_found == 0) {
            if(this.racers.length > 2) {
                var middleindex = Math.floor(this.racers.length / 2);
                final_racer = this.racers[middleindex];
            }
        }
        return final_racer;
    }

    /**
     * Get the first racer
     * @returns {Racer} The racer currently in the first place
     */
    get first() {
        let furthest = 0;
        let shortest = 1000000000000000;
        let first = null;

        for (let racer = 0, racerCount = this.racers.length; racer < racerCount; ++racer) {
            if(this.race_type == 'reverse') {
                if (first === null || this.racers[racer].moved < shortest) {
                    first = this.racers[racer];
                    shortest = this.racers[racer].moved;
                }
            } else {
                if (first === null || this.racers[racer].moved > furthest) {
                    first = this.racers[racer];
                    furthest = this.racers[racer].moved;
                }
            }
        }

        return first;
    }

    /**
     * Get the current_racer_info
     * @returns {Racer} The player racer
     */
     get boostingRacers() {
        const racers = [];

        for (let racer = 0, racerCount = this.racers.length; racer < racerCount; ++racer)
            racers.push(this.racers[racer].aggression_boost);

        return racers;
    }
    /**
     * Load racers
     * @param {WebGLRenderer} renderer The renderer
     */
    load(renderer) {
        for (const racer of this.racers)
            racer.load(renderer).then(() => {
                this.scene.add(racer.ship);
                this.scene.add(racer.trail.mesh);

                const posName = Object.keys(this.audioPlayer.posloaded);
                this.audioPlayer.setPosDistance(100, 250);
                this.audioPlayer.setPosCone(45, 120, 1);
                // const shipsound = posName[Math.floor(Math.random()*posName.length)];
                
                const index = Math.floor(Math.random()*posName.length)
              
                this.sound = posName[index]

                this.sounds.push(this.sound);
                racer.ship.add(this.audioPlayer.posloaded[this.sound])

            });
    }

    /**
     * Update the racers state
     * @param {number} delta The time delta
     */
    update(delta) {
        if (!this.started)
            return;

        for (let racer = 0, racerCount = this.racers.length; racer < racerCount; ++racer) {
            this.racers[racer].update(delta);

            // TODO: What's going on here
            const sounds = Object.values(this.audioPlayer.posloaded);
            // console.log(sounds);

            sounds.forEach(element => {
                setInterval(() => {
                    element.rotation.y += Math.random();
                }, 2000);

                this.audioPlayer.setVolume(element, Math.sin(delta))
            });

            if (this.racers[racer].finished === -1 &&
                this.racers[racer].moved === -1) {
                this.racers[racer].setFinished();

                this.finishedRacers.push(this.racers[racer]);
                this.finishedRacers.sort((a, b) => a.finished - b.finished);

                this.finishedOrder = [];

                /** This finished portion in racers.js does not get triggered */

                for (let i = 0; i < this.sounds.length; i++) {
                    this.audioPlayer.pauseSound(this.sounds[i])
                }

                for (let finished = 0, finishedCount = this.finishedRacers.length; finished < finishedCount; ++finished)
                    this.finishedOrder.push(this.racers.indexOf(this.finishedRacers[finished]));

                this.onFinish(this.racers[racer], this.finishedRacers.length === this.racers.length);
            }
        }

        this.leader.update(delta, this.first);
    }

    /**
     * Render a frame
     * @param {number} time The time interpolation in the range [0, 1]
     * @param {Blip[]} blips The blips to update
     */
    render(time, blips) {
        for (let racer = 0, racerCount = this.racers.length; racer < racerCount; ++racer)
            this.racers[racer].render(time, blips ? blips[racer] : null);
    }

    /**
     * Start the race
     */
    start() {
        this.started = true;
        
        this.playRacecraftSounds();
    }
    
    /* START RACECRAFT SOUNDS */
    playRacecraftSounds() {
        for (let i = 0; i < this.sounds.length; i++) {
            this.audioPlayer.playPosSound(this.sounds[i]);
            //console.log(this.sounds[i]);
        }
    }

    /* STOP RACECRAFT SOUNDS */
    pauseRacecraftSounds() {
        for (let i = 0; i < this.sounds.length; i++) {
            this.audioPlayer.pausePosSound(this.sounds[i]);
        }
    }

    /* MUTE RACECRAFT SOUNDS */
    muteRacecraftSounds() {
        for (let i = 0; i < this.sounds.length; i++) {
            this.audioPlayer.setPosDistance(1,1);
            this.audioPlayer.setPosCone(1, 1, 1);
        }
    }

    unmuteRacecraftSounds() {
        for (let i = 0; i < this.sounds.length; i++) {
            this.audioPlayer.setPosDistance(100, 250);
            this.audioPlayer.setPosCone(45, 120, 1);
        }
    }
}